import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TableModule, TableLazyLoadEvent } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { CalendarModule } from 'primeng/calendar';
import { DrawerModule } from 'primeng/drawer';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { finalize } from 'rxjs/operators';
import { AuthStore } from '@core/service/auth-store.service';
import { HasAnyRoleDirective } from '@core/directives/has-any-role.directive';
import { BackButtonComponent } from '@app/shared/components/back-button/back-button.component';
import { DataVoidComponent } from '@app/shared/components/data-void/data-void.component';
import { toApiDateString } from '@shared/utils';
import { PeriodoRenovacaoMatriculaService } from '../../api/services/periodo-renovacao-matricula.service';
import { PeriodoRenovacaoDrawerComponent } from '../periodo-renovacao-drawer/periodo-renovacao-drawer.component';
import {
  AnoLectivoOption,
  PeriodoRenovacaoMatricula,
  PeriodoRenovacaoMatriculaListParams,
  TIPO_BOLSA_OPTIONS,
  TipoBolsaOption,
} from '../../api/models/periodo-renovacao-matricula.model';

/**
 * Roles/permissões Keycloak que dão acesso a esta configuração.
 * Ligar aqui à role/claim correta — mantém-se centralizado neste único sítio.
 */
export const PERIODO_RENOVACAO_PERMISSIONS = ['CONFIGURAÇÕES', 'PERÍODO DE RENOVAÇÃO'];

@Component({
  selector: 'app-lista-periodo-renovacao',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TableModule,
    ButtonModule,
    SelectModule,
    CalendarModule,
    DrawerModule,
    TagModule,
    ToastModule,
    HasAnyRoleDirective,
    BackButtonComponent,
    DataVoidComponent,
    PeriodoRenovacaoDrawerComponent,
  ],
  providers: [MessageService, PeriodoRenovacaoMatriculaService],
  templateUrl: './lista-periodo-renovacao.component.html',
  styleUrl: './lista-periodo-renovacao.component.scss',
})
export class ListaPeriodoRenovacaoComponent implements OnInit {
  private fb = inject(FormBuilder);
  private periodoService = inject(PeriodoRenovacaoMatriculaService);
  private messageService = inject(MessageService);
  private authStore = inject(AuthStore);

  readonly permissoes = PERIODO_RENOVACAO_PERMISSIONS;

  periodos: PeriodoRenovacaoMatricula[] = [];
  loading = false;
  showDrawer = false;
  showFilterDrawer = false;

  pageNumber = 1;
  pageSize = 10;
  totalRecords = 0;

  filterForm!: FormGroup;

  anosLectivos: AnoLectivoOption[] = [];
  anosLectivosAtivos: AnoLectivoOption[] = [];
  private anoLectivoLabelById = new Map<number, string>();

  tipoBolsaOptions: TipoBolsaOption[] = TIPO_BOLSA_OPTIONS;

  ngOnInit(): void {
    this.filterForm = this.fb.group({
      anoLectivo: [null],
      tipoBolsaEnum: [null],
      dataInicio: [null],
      dataFim: [null],
    });

    this.loadAnosLectivos();
    this.fetch();
  }

  get canManage(): boolean {
    return this.authStore.hasAny(this.permissoes);
  }

  private loadAnosLectivos(): void {
    this.periodoService.getAnosLectivos().subscribe({
      next: (anos) => {
        this.anosLectivos = anos ?? [];
        this.anosLectivosAtivos = this.anosLectivos.filter((a) => a.activo === true);
        this.anoLectivoLabelById = new Map(this.anosLectivos.map((a) => [a.id, a.ano]));
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Não foi possível carregar os anos lectivos.',
        });
      },
    });
  }

  anoLectivoLabel(anoLectivoId: number): string {
    return this.anoLectivoLabelById.get(anoLectivoId) ?? '—';
  }

  get minAnoLectivoDate(): Date | null {
    return this.anoLectivoLimitDate(Math.min);
  }

  get maxAnoLectivoDate(): Date | null {
    return this.anoLectivoLimitDate(Math.max);
  }

  private anoLectivoLimitDate(operation: (...values: number[]) => number): Date | null {
    const years = this.anosLectivos
      .map((item) => Number(item.ano))
      .filter((year) => Number.isInteger(year));

    return years.length ? new Date(operation(...years), 0, 1) : null;
  }

  /** 'yyyy-MM-dd' -> 'dd/MM/yyyy', sem passar por Date (evita risco de shift de timezone). */
  toDisplayDate(value: string | null | undefined): string {
    if (!value) return '—';
    const [year, month, day] = value.split('-');
    return year && month && day ? `${day}/${month}/${year}` : value;
  }

  get hasActivoColumn(): boolean {
    return this.periodos.some((p) => p.activo !== undefined);
  }

  fetch(): void {
    this.loading = true;

    const raw = this.filterForm.getRawValue();
    const selectedYear = raw.anoLectivo instanceof Date ? raw.anoLectivo.getFullYear().toString() : null;
    const anoLectivoId = this.anosLectivos.find((item) => item.ano === selectedYear)?.id;
    const params: PeriodoRenovacaoMatriculaListParams = {
      PageNumber: this.pageNumber,
      PageSize: this.pageSize,
      OrderBy: 'id desc',
      AnoLectivoId: anoLectivoId,
      TipoBolsaEnum: raw.tipoBolsaEnum ?? undefined,
      DataInicio: toApiDateString(raw.dataInicio) ?? undefined,
      DataFim: toApiDateString(raw.dataFim) ?? undefined,
    };

    this.periodoService
      .listarPeriodos(params)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (res) => {
          this.periodos = res?.data ?? [];
          this.totalRecords = res?.totalRecords ?? 0;
        },
        error: () => {
          this.periodos = [];
          this.totalRecords = 0;
          this.messageService.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'Não foi possível carregar os períodos de renovação.',
          });
        },
      });
  }

  applyFilters(): void {
    this.pageNumber = 1;
    this.showFilterDrawer = false;
    this.fetch();
  }

  clearFilters(): void {
    this.filterForm.reset({
      anoLectivo: null,
      tipoBolsaEnum: null,
      dataInicio: null,
      dataFim: null,
    });
    this.pageNumber = 1;
    this.fetch();
  }

  onPageChange(event: TableLazyLoadEvent): void {
    const rows = event.rows ?? this.pageSize;
    const first = event.first ?? 0;
    this.pageSize = rows;
    this.pageNumber = Math.floor(first / rows) + 1;
    this.fetch();
  }

  openNovoPeriodo(): void {
    if (!this.canManage) return;
    this.showDrawer = true;
  }

  tipoBolsaLabel(periodo: PeriodoRenovacaoMatricula): string {
    return periodo.tipoBolsa
      || this.tipoBolsaOptions.find((option) => option.value === periodo.tipoBolsaEnum)?.label
      || '—';
  }

  onDrawerClosed(saved: boolean): void {
    this.showDrawer = false;
    if (saved) {
      this.pageNumber = 1;
      this.fetch();
    }
  }
}
